import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route } from "react-router-dom";
import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  isPlatform,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import {
  homeOutline,
  documentOutline,
  informationCircleOutline,
} from "ionicons/icons";
import Home from "../pages/Home";
import ProjectTiers from "../pages/ProjectTiers";
import ProjectList from "../pages/ProjectList";
import Instruction from "../pages/Instruction";
import About from "../pages/About";
import Privacy from "../pages/Privacy";
import * as actions from "../store/actions";
import { StateInterface } from "../interfaces/StateInterface";
import { ErrorRefs } from "../ErrorRefs";

const Main: React.FC = () => {
  const dispatch = useDispatch();
  let numGridCols: number = useSelector(
    (state: StateInterface) => state.layout.numGridCols
  );

  const fetchProjectsJSON = useCallback(() => {
    fetch(
      "https://storage.googleapis.com/snappyxo-projects/SnappyXOProjects.json",
      {
        method: "GET",
        cache: "no-cache",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error("Server maintenance. Please check back later.");
        }
      })
      .then((json) => {
        dispatch(actions.setError(null));
        dispatch(actions.setJSON(json));
        dispatch(actions.setLoaded(true));
      })
      .catch(() => {
        dispatch(actions.setError(ErrorRefs["Failed to load JSON file"]));
      });
  }, [dispatch]);

  useEffect(() => {
    fetchProjectsJSON();
  }, [fetchProjectsJSON]);

  useEffect(() => {
    const handleResize = () => {
      const currentNumGridCols =
        Math.floor(window.innerWidth / 150) < 1
          ? 1
          : Math.floor(window.innerWidth / 150) > 12
          ? 12
          : Math.floor(window.innerWidth / 150);
      if (currentNumGridCols !== numGridCols) {
        dispatch(actions.setNumGridCols(currentNumGridCols));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch, numGridCols]);

  //**Important**temporary turn off animation on ios due to it conflict with safari swipe back gesture
  return (
    <IonApp>
      <IonReactRouter>
        <IonTabs>
          <IonRouterOutlet
            animated={!isPlatform("ios") || isPlatform("capacitor")}
          >
            <Route exact path="/home" render={() => <Home />} />
            <Route
              exact
              path="/projects"
              render={(props) => (
                <ProjectTiers
                  {...props}
                  fetchProjectsJSON={fetchProjectsJSON}
                />
              )}
            />
            <Route
              exact
              path="/projects/:tier"
              render={(props) => (
                <ProjectList {...props} fetchProjectsJSON={fetchProjectsJSON} />
              )}
            />
            <Route
              exact
              path="/projects/:tier/:title"
              render={(props) => (
                <Instruction {...props} fetchProjectsJSON={fetchProjectsJSON} />
              )}
            />
            <Route exact path="/about" render={() => <About />} />
            <Route exact path="/privacy" render={() => <Privacy />} />
            <Redirect exact from="/" to="/home" />
          </IonRouterOutlet>
          <IonTabBar slot="bottom">
            <IonTabButton tab="home" href="/home">
              <IonIcon icon={homeOutline} />
              <IonLabel>Home</IonLabel>
            </IonTabButton>
            <IonTabButton tab="projects" href="/projects">
              <IonIcon icon={documentOutline} />
              <IonLabel>Projects</IonLabel>
            </IonTabButton>
            <IonTabButton tab="about" href="/about">
              <IonIcon icon={informationCircleOutline} />
              <IonLabel>About</IonLabel>
            </IonTabButton>
          </IonTabBar>
        </IonTabs>
      </IonReactRouter>
    </IonApp>
  );
};

export default Main;
